/**
 * Yearly Financial Summary Report
 * Comprehensive annual financial overview with monthly breakdown
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  RefreshCw,
  Award,
  Target,
  Percent,
  ShoppingCart,
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  Smartphone,
  HeartPulse,
  CheckCircle2,
  XCircle,
  Loader2,
  Pencil,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return `R ${(value || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCurrencyShort = (value) => {
  if (value >= 1000000) {
    return `R ${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `R ${(value / 1000).toFixed(1)}K`;
  }
  return `R ${value.toFixed(0)}`;
};

export const YearlyFinancialSummary = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [healthCheck, setHealthCheck] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  
  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/financial-summary/yearly`, {
        params: { year: selectedYear }
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching yearly summary:", error);
      toast.error("Failed to load yearly financial summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const runHealthCheck = async () => {
    setHealthLoading(true);
    setHealthOpen(true);
    try {
      const response = await axios.get(`${API}/financial-summary/health-check`, {
        params: { year: selectedYear }
      });
      setHealthCheck(response.data);
    } catch (error) {
      console.error("Health check failed:", error);
      toast.error("Failed to run health check");
    } finally {
      setHealthLoading(false);
    }
  };

  const openEditModal = async () => {
    // Pre-populate the editor from the currently displayed monthly_data
    // (override values if present, otherwise the live computed values).
    if (!data?.monthly_data) {
      toast.warning("Wait for the yearly summary to load before editing.");
      return;
    }
    try {
      const res = await axios.get(`${API}/financial-summary/yearly/overrides/${selectedYear}`);
      const overrideMap = {};
      (res.data?.overrides || []).forEach(o => { overrideMap[o.month] = o; });
      const rows = data.monthly_data.map(m => {
        const ov = overrideMap[m.month] || {};
        return {
          month: m.month,
          month_name: m.month_name,
          // What the user enters — pre-fill with override if set, else current displayed value.
          turnover: (ov.turnover_incl_vat !== undefined && ov.turnover_incl_vat !== null) ? ov.turnover_incl_vat : m.turnover,
          gross_profit: (ov.gross_profit !== undefined && ov.gross_profit !== null) ? ov.gross_profit : m.gross_profit,
          expenses: (ov.expenses !== undefined && ov.expenses !== null) ? ov.expenses : m.expenses,
          notes: ov.notes || "",
          // Snapshot of the live computed values for the "Live" comparison column.
          live_turnover: m.is_override ? null : m.turnover,
          live_gross_profit: m.is_override ? null : m.gross_profit,
          live_expenses: m.is_override ? null : m.expenses,
          is_override_existing: !!overrideMap[m.month],
        };
      });
      setEditRows(rows);
      setEditOpen(true);
    } catch (e) {
      console.error("Failed to load overrides:", e);
      toast.error("Failed to open editor");
    }
  };

  const updateEditRow = (idx, field, value) => {
    setEditRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const resetMonthOverride = async (month) => {
    setEditSaving(true);
    try {
      await axios.delete(`${API}/financial-summary/yearly/overrides/${selectedYear}/${month}`);
      toast.success(`Override cleared for month ${month} — using live data.`);
      // Re-open with fresh data
      await fetchData();
      // Re-pull modal data after refresh
      setTimeout(openEditModal, 100);
    } catch (e) {
      console.error("Failed to reset override:", e);
      toast.error("Failed to reset override");
    } finally {
      setEditSaving(false);
    }
  };

  const saveAllOverrides = async () => {
    setEditSaving(true);
    try {
      // Save one row per month — backend upserts. If user blanked all 3 values,
      // backend treats it as a delete.
      for (const r of editRows) {
        const payload = {
          turnover_incl_vat: r.turnover === "" || r.turnover === null ? null : parseFloat(r.turnover),
          gross_profit: r.gross_profit === "" || r.gross_profit === null ? null : parseFloat(r.gross_profit),
          expenses: r.expenses === "" || r.expenses === null ? null : parseFloat(r.expenses),
          notes: r.notes || null,
        };
        // If unchanged compared to live AND no existing override, skip (avoid creating noise).
        const unchanged = (
          !r.is_override_existing &&
          payload.turnover_incl_vat === r.live_turnover &&
          payload.gross_profit === r.live_gross_profit &&
          payload.expenses === r.live_expenses &&
          !(r.notes || "").trim()
        );
        if (unchanged) continue;
        await axios.put(
          `${API}/financial-summary/yearly/overrides/${selectedYear}/${r.month}`,
          payload
        );
      }
      toast.success("Totals saved");
      setEditOpen(false);
      await fetchData();
    } catch (e) {
      console.error("Save failed:", e);
      toast.error(e.response?.data?.detail || "Failed to save totals");
    } finally {
      setEditSaving(false);
    }
  };

  // Prepare chart data
  const chartData = data?.monthly_data?.map(m => ({
    name: m.month_short,
    turnover: m.turnover,
    grossProfit: m.gross_profit,
    expenses: m.expenses,
    netProfit: m.net_profit,
  })) || [];

  return (
    <div className="p-6 space-y-6" data-testid="yearly-summary-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-7 w-7 text-purple-600" />
            Yearly Financial Summary
          </h1>
          <p className="text-gray-500 mt-1">
            Annual financial performance overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => navigate("/yearly-summary/all-time")}
            data-testid="all-time-nav-btn"
            className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-900 hover:from-amber-100 hover:to-yellow-100"
          >
            <Sparkles className="h-4 w-4 mr-2 text-amber-600" />
            All-Time
          </Button>
          <Button onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={openEditModal} data-testid="edit-totals-btn">
            <Pencil className="h-4 w-4 mr-2 text-blue-600" />
            Edit Totals
          </Button>
          <Button variant="outline" onClick={runHealthCheck} disabled={healthLoading} data-testid="health-check-btn">
            {healthLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HeartPulse className="h-4 w-4 mr-2 text-rose-500" />}
            Health Check
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading yearly summary...</div>
      ) : data ? (
        <>
          {/* Year-to-Date Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Annual Turnover</p>
                    <p className="text-2xl font-bold" data-testid="ytd-turnover">
                      {formatCurrency(data.year_to_date.turnover)}
                    </p>
                    <p className="text-xs text-blue-200 mt-1">
                      Avg: {formatCurrency(data.year_to_date.average_monthly_turnover)}/mo
                    </p>
                  </div>
                  <DollarSign className="h-10 w-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Gross Profit</p>
                    <p className="text-2xl font-bold" data-testid="ytd-gross-profit">
                      {formatCurrency(data.year_to_date.gross_profit)}
                    </p>
                    <p className="text-xs text-green-200 mt-1">
                      GP: {data.year_to_date.gp_percent.toFixed(1)}%
                    </p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm">Total Expenses</p>
                    <p className="text-2xl font-bold" data-testid="ytd-expenses">
                      {formatCurrency(data.year_to_date.expenses)}
                    </p>
                    <p className="text-xs text-red-200 mt-1">
                      Cost of Sales: {formatCurrency(data.year_to_date.cost_of_goods_sold)}
                    </p>
                  </div>
                  <Wallet className="h-10 w-10 text-red-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Net Profit</p>
                    <p className="text-2xl font-bold" data-testid="ytd-net-profit">
                      {formatCurrency(data.year_to_date.net_profit)}
                    </p>
                    <p className="text-xs text-purple-200 mt-1">
                      Net Margin: {data.year_to_date.net_margin.toFixed(1)}%
                    </p>
                  </div>
                  <Target className="h-10 w-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500">Transactions</p>
                    <p className="text-xl font-bold">{data.year_to_date.transaction_count.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <BarChart3 className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-xs text-gray-500">Items Sold</p>
                    <p className="text-xl font-bold">{Math.round(data.year_to_date.items_sold).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-500">Supplier Payments</p>
                    <p className="text-xl font-bold">{formatCurrencyShort(data.year_to_date.supplier_payments)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-8 w-8 text-purple-500" />
                  <div>
                    <p className="text-xs text-gray-500">Service Revenue</p>
                    <p className="text-xl font-bold">{formatCurrencyShort(data.year_to_date.service_revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Highlights */}
          {data.highlights && (data.highlights.best_month || data.highlights.most_profitable_month) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.highlights.best_month && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-full">
                        <Award className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-green-600 font-medium">Best Sales Month</p>
                        <p className="text-lg font-bold text-green-800">
                          {data.highlights.best_month.month_name}
                        </p>
                        <p className="text-sm text-green-700">
                          {formatCurrency(data.highlights.best_month.turnover)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {data.highlights.most_profitable_month && (
                <Card className="border-2 border-blue-200 bg-blue-50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <TrendingUp className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Most Profitable Month</p>
                        <p className="text-lg font-bold text-blue-800">
                          {data.highlights.most_profitable_month.month_name}
                        </p>
                        <p className="text-sm text-blue-700">
                          {formatCurrency(data.highlights.most_profitable_month.gross_profit)} ({data.highlights.most_profitable_month.gp_percent.toFixed(1)}% GP)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {data.highlights.worst_month && (
                <Card className="border-2 border-amber-200 bg-amber-50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-full">
                        <TrendingDown className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-amber-600 font-medium">Lowest Sales Month</p>
                        <p className="text-lg font-bold text-amber-800">
                          {data.highlights.worst_month.month_name}
                        </p>
                        <p className="text-sm text-amber-700">
                          {formatCurrency(data.highlights.worst_month.turnover)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Turnover & Profit Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Monthly Turnover & Profit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => formatCurrencyShort(v)} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ color: '#333' }}
                    />
                    <Legend />
                    <Bar dataKey="turnover" name="Turnover" fill="#3b82f6" />
                    <Bar dataKey="grossProfit" name="Gross Profit" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Profit Trend Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Profit Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => formatCurrencyShort(v)} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ color: '#333' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="grossProfit" 
                      name="Gross Profit" 
                      stroke="#22c55e" 
                      strokeWidth={2}
                      dot={{ fill: '#22c55e' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="netProfit" 
                      name="Net Profit" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      dot={{ fill: '#8b5cf6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Monthly Breakdown - {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Turnover</TableHead>
                      <TableHead className="text-right">COGS</TableHead>
                      <TableHead className="text-right">Gross Profit</TableHead>
                      <TableHead className="text-right">GP%</TableHead>
                      <TableHead className="text-right">Expenses</TableHead>
                      <TableHead className="text-right">Net Profit</TableHead>
                      <TableHead className="text-right">Txns</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.monthly_data.map((month) => (
                      <TableRow 
                        key={month.month}
                        className={month.turnover === 0 ? "text-gray-400" : ""}
                      >
                        <TableCell className="font-medium">
                          {month.month_name}
                          {month.is_override && (
                            <Badge
                              variant="outline"
                              className="ml-2 border-blue-400 text-blue-700 text-[10px]"
                              title={month.override_note || "Manual override applied"}
                            >
                              override
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(month.turnover)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(month.cost_of_goods_sold)}</TableCell>
                        <TableCell className="text-right text-green-600 font-semibold">
                          {formatCurrency(month.gross_profit)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={month.gp_percent >= 20 ? "default" : month.gp_percent >= 10 ? "secondary" : "outline"}>
                            {month.gp_percent.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-orange-600">{formatCurrency(month.expenses)}</TableCell>
                        <TableCell className={`text-right font-semibold ${month.net_profit >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                          {formatCurrency(month.net_profit)}
                        </TableCell>
                        <TableCell className="text-right">{month.transaction_count}</TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.year_to_date.turnover)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(data.year_to_date.cost_of_goods_sold)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(data.year_to_date.gross_profit)}</TableCell>
                      <TableCell className="text-right">
                        <Badge>{data.year_to_date.gp_percent.toFixed(1)}%</Badge>
                      </TableCell>
                      <TableCell className="text-right text-orange-600">{formatCurrency(data.year_to_date.expenses)}</TableCell>
                      <TableCell className={`text-right ${data.year_to_date.net_profit >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                        {formatCurrency(data.year_to_date.net_profit)}
                      </TableCell>
                      <TableCell className="text-right">{data.year_to_date.transaction_count}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-16 text-gray-500">
          No data available for {selectedYear}
        </div>
      )}

      {/* Health Check Dialog */}
      <Dialog open={healthOpen} onOpenChange={setHealthOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="health-check-title">
              <HeartPulse className="h-5 w-5 text-rose-500" />
              Financial Health Check — {selectedYear}
            </DialogTitle>
          </DialogHeader>

          {healthLoading ? (
            <div className="flex items-center justify-center py-12 gap-3 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Running health check across all months...
            </div>
          ) : healthCheck ? (
            <div className="space-y-4">
              {/* Overall Status */}
              <div
                className={`flex items-center gap-3 p-4 rounded-lg ${
                  healthCheck.overall_status === "healthy"
                    ? "bg-green-50 border border-green-200"
                    : "bg-red-50 border border-red-200"
                }`}
                data-testid="health-check-status"
              >
                {healthCheck.overall_status === "healthy" ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600 shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-sm">
                    {healthCheck.overall_status === "healthy"
                      ? "All Clear — Reports are consistent"
                      : "Discrepancies Found"}
                  </p>
                  <p className="text-xs text-gray-600">
                    {healthCheck.months_checked} month(s) checked &middot; Total GP difference: {formatCurrency(healthCheck.total_gp_difference)}
                  </p>
                </div>
              </div>

              {/* Per-Month Results */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 text-xs">
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Monthly GP</TableHead>
                      <TableHead className="text-right">Yearly GP</TableHead>
                      <TableHead className="text-right">Diff</TableHead>
                      <TableHead className="text-right">Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {healthCheck.checks.map((c) => (
                      <TableRow key={c.month} data-testid={`health-row-${c.month}`}>
                        <TableCell>
                          {c.status === "pass" ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium text-sm">{c.month_name}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(c.monthly_gp)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(c.yearly_gp)}</TableCell>
                        <TableCell className={`text-right text-sm font-medium ${c.gp_difference > 0 ? "text-red-600" : "text-green-600"}`}>
                          {formatCurrency(c.gp_difference)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="text-xs capitalize">
                            {c.data_source_yearly}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {healthCheck.checks.length === 0 && (
                <p className="text-center text-gray-400 py-6 text-sm">No months with sales data found for {selectedYear}.</p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Totals modal — manual monthly override for the selected year */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-totals-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Edit {selectedYear} Totals
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 -mt-1 mb-2">
            Manually correct turnover, gross profit and expenses for any month — useful when
            live data is incomplete (e.g. migrated from another POS). Live months not touched
            here keep their computed values. Blank a field to clear that override.
          </div>
          <div className="border rounded">
            <Table>
              <TableHeader className="bg-gray-50 sticky top-0">
                <TableRow>
                  <TableHead className="w-24">Month</TableHead>
                  <TableHead className="text-right">Turnover (incl VAT)</TableHead>
                  <TableHead className="text-right">Gross Profit</TableHead>
                  <TableHead className="text-right">Expenses</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editRows.map((r, idx) => (
                  <TableRow key={r.month} className={r.is_override_existing ? "bg-blue-50/40" : ""}>
                    <TableCell className="font-medium">
                      {r.month_name}
                      {r.is_override_existing && (
                        <Badge variant="outline" className="ml-2 border-blue-400 text-blue-700 text-[10px]">
                          override
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.turnover ?? ""}
                        onChange={(e) => updateEditRow(idx, "turnover", e.target.value)}
                        className="text-right font-mono h-8"
                        data-testid={`edit-turnover-${r.month}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.gross_profit ?? ""}
                        onChange={(e) => updateEditRow(idx, "gross_profit", e.target.value)}
                        className="text-right font-mono h-8"
                        data-testid={`edit-gp-${r.month}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.expenses ?? ""}
                        onChange={(e) => updateEditRow(idx, "expenses", e.target.value)}
                        className="text-right font-mono h-8"
                        data-testid={`edit-exp-${r.month}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={r.notes}
                        onChange={(e) => updateEditRow(idx, "notes", e.target.value)}
                        placeholder="(optional)"
                        className="h-8 text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      {r.is_override_existing && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-600"
                          title="Clear override (revert to live data)"
                          onClick={() => resetMonthOverride(r.month)}
                          disabled={editSaving}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={saveAllOverrides} disabled={editSaving} data-testid="save-totals-btn">
              {editSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Save Totals
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YearlyFinancialSummary;
